import { LoginPage } from '../../features/auth/pages/LoginPage';
import { getAuthToken, setAuthToken } from './api';

export interface User {
  id: string;
  email: string;
  name: string;
  /**
   * Slug do perfil. Deixou de ser união fechada quando os perfis passaram a
   * ser dados: o Administrador cria novos pela tela `/admin/perfis`, sem
   * deploy. Autorização é por permissão (`usePermissions`), não por slug —
   * comparar com slug só sobrevive na escolha do layout dos portais.
   */
  role: string;
  /** Id do perfil na tabela `roles`. */
  roleId?: string | null;
  companyId?: string;
  managerId?: string;
  supplierId?: string;
  /** Cliente do usuário externo (perfil `cliente`). */
  clientId?: string | null;
  /** Regime de contratação — presente apenas para colaboradores. */
  employmentType?: 'clt' | 'pj' | null;
  isFirstLogin?: boolean;
  mustChangePassword?: boolean;
}

export interface AuthProviders {
  google: { enabled: boolean; clientId?: string };
  github: { enabled: boolean; clientId?: string };
}

const REFRESH_TOKEN_KEY = 'refresh_token';

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function clearRefreshToken(): void {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export async function getAuthProviders(): Promise<AuthProviders> {
  const response = await fetch('/api/public/auth/providers');
  if (!response.ok) {
    throw new Error('Falha ao buscar providers de autenticação');
  }
  return response.json();
}

class AuthService {
  private currentUser: User | null = null;
  private listeners: Array<(user: User | null) => void> = [];

  constructor() {
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      this.currentUser = JSON.parse(savedUser);
    }
  }

  addAuthListener(listener: (user: User | null) => void) {
    this.listeners.push(listener);
  }

  removeAuthListener(listener: (user: User | null) => void) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.currentUser));
  }

  async login(email: string, password: string): Promise<User> {
    const { authApi } = await import('./api');
    const userData = await authApi.login(email, password);

    const user: User = {
      id: userData.id,
      email: userData.email,
      name: userData.name,
      role: userData.role,
      companyId: userData.companyId,
      managerId: userData.managerId,
      supplierId: userData.supplierId,
      clientId: userData.clientId ?? null,
      employmentType: userData.employmentType ?? null,
      isFirstLogin: userData.isFirstLogin,
      mustChangePassword: userData.mustChangePassword ?? userData.isFirstLogin,
    };

    this.currentUser = user;
    localStorage.setItem('currentUser', JSON.stringify(this.currentUser));

    if (userData.refreshToken) {
      setRefreshToken(userData.refreshToken);
    }

    this.notifyListeners();
    return this.currentUser;
  }

  logout() {
    this.currentUser = null;
    localStorage.removeItem('currentUser');
    clearRefreshToken();
    setAuthToken(null);
    this.notifyListeners();
  }

  getCurrentUser(): User | null {
    return this.currentUser;
  }

  isAuthenticated(): boolean {
    const token = getAuthToken();
    return this.currentUser !== null && token !== null;
  }

  async changePassword(email: string, currentPassword: string, newPassword: string): Promise<void> {
    if (!this.currentUser) throw new Error('Usuário não autenticado');
    const { authApi } = await import('./api');
    await authApi.changePassword(currentPassword, newPassword);
    if (this.currentUser) {
      this.currentUser.isFirstLogin = false;
      this.currentUser.mustChangePassword = false;
      localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
      this.notifyListeners();
    }
  }

  checkFirstAccess(email: string): boolean {
    return localStorage.getItem('first_login_' + email) === 'true';
  }

  clearFirstAccess(email: string): void {
    localStorage.removeItem('first_login_' + email);
  }
}

export const authService = new AuthService();

export { LoginPage };
